import { useEffect } from 'react'
import { RoomEvent, Track, type Room } from 'livekit-client'
import { getSocket } from '../services/socket'
import { formatPhoneDisplay } from '../utils/formatPhone'

const CHUNK_INTERVAL_MS = 250
const HEALTH_MS = 3000

/**
 * PSTN phone-leg transcription feeder. The SIP participant has no client
 * of its own, so the call OWNER's browser records the remote phone audio
 * track and streams it to the server as a second transcript stream
 * (`remote: true` → `${sessionId}#remote` Deepgram session + its own
 * webm in the voice-audio archive under speaker id `phone_<digits>`).
 *
 * Pass transcriptSessionId/phoneNumber only while the call is connected —
 * undefined/null tears the recorder down (same contract as the args
 * becoming stale). Server-side authz: only the live PhoneCallSession's
 * owner may feed this stream (socket.ts transcript:start remoteSpeaker).
 *
 * NOT a mediaManager surface: this clones a REMOTE subscribed track for
 * a local MediaRecorder — no local capture, no getUserMedia, no publish.
 */
export function useRemotePhoneTranscription(
  transcriptSessionId: string | undefined,
  room: Room | null,
  phoneNumber: string | null,
) {
  useEffect(() => {
    if (!transcriptSessionId || !room || !phoneNumber) return
    const digits = phoneNumber.replace(/\D/g, '')
    if (!digits) return
    const speakerId = `phone_${digits}`
    const speakerName = formatPhoneDisplay(phoneNumber)
    const socket = getSocket()
    let recorder: MediaRecorder | null = null
    let cloned: MediaStreamTrack | null = null
    let started = false
    let disposed = false
    let unsupported = false

    const findPhoneTrack = (): MediaStreamTrack | null => {
      for (const p of room.remoteParticipants.values()) {
        if (!(p.identity.startsWith('phone:') || p.identity.startsWith('sip_'))) continue
        const pub = p.getTrackPublication(Track.Source.Microphone)
        const t = pub?.track?.mediaStreamTrack
        if (t && t.readyState === 'live') return t
      }
      return null
    }

    const stopLocal = () => {
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop() } catch { /* noop */ }
      }
      recorder = null
      if (cloned) {
        try { cloned.stop() } catch { /* noop */ }
        cloned = null
      }
      started = false
    }

    const tryStart = () => {
      if (disposed || started || unsupported) return
      const src = findPhoneTrack()
      if (!src) return
      try {
        cloned = src.clone()
      } catch (err) {
        console.warn('[RemotePhoneTx] track.clone failed:', err)
        return
      }
      let rec: MediaRecorder
      try {
        rec = new MediaRecorder(new MediaStream([cloned]), { mimeType: 'audio/webm;codecs=opus' })
      } catch (err) {
        console.warn('[RemotePhoneTx] MediaRecorder unsupported:', err)
        unsupported = true
        try { cloned.stop() } catch { /* noop */ }
        cloned = null
        return
      }
      recorder = rec
      // Idempotent server-side: re-emitting start resets the #remote session.
      socket.emit('transcript:start', {
        channelId: transcriptSessionId,
        remoteSpeaker: { id: speakerId, name: speakerName },
      })
      rec.ondataavailable = async (event) => {
        if (disposed) return
        if (!event.data || event.data.size === 0) return
        try {
          const buf = await event.data.arrayBuffer()
          // volatile: drop (never queue) chunks while the socket is down —
          // socket.io's unbounded sendBuffer ate gigabytes on half-dead
          // clients (2026-08-06). See useTranscription.ts.
          socket.volatile.emit('transcript:audio-chunk', { channelId: transcriptSessionId, chunk: buf, remote: true, remoteSpeakerId: speakerId })
        } catch (err) {
          console.warn('[RemotePhoneTx] chunk forward failed:', err)
        }
      }
      try {
        rec.start(CHUNK_INTERVAL_MS)
      } catch (err) {
        console.warn('[RemotePhoneTx] recorder.start failed:', err)
        stopLocal()
        try { socket.emit('transcript:stop', { channelId: transcriptSessionId, remote: true, remoteSpeakerId: speakerId }) } catch { /* noop */ }
        return
      }
      started = true
      console.info(`[RemotePhoneTx] recorder started (session=${transcriptSessionId}, speaker=${speakerId})`)
    }

    tryStart()
    // Subscription churn while recording = the upstream track may have
    // been replaced (SIP re-INVITE / transfer). A clone of the OLD track
    // can stay readyState='live' while the upstream goes silent — the
    // health check can't see that. Rebind on real churn instead; the
    // server-side transcript:start is an idempotent reset.
    const onTrack = () => {
      if (disposed) return
      if (started) stopLocal()
      tryStart()
    }
    room.on(RoomEvent.TrackSubscribed, onTrack)
    room.on(RoomEvent.TrackUnsubscribed, onTrack)
    room.on(RoomEvent.ParticipantConnected, onTrack)
    // Health check: remote track replaced/ended (SIP re-INVITE etc.) —
    // tear down the stale recorder and rebind. Mirrors useTranscription's
    // recovery loop, minus the local-publish cases (no denoiser swaps on
    // remote tracks).
    const health = window.setInterval(() => {
      if (disposed) return
      if (started && (!recorder || recorder.state !== 'recording' || !cloned || cloned.readyState !== 'live')) {
        stopLocal()
        tryStart()
      } else if (!started) {
        tryStart()
      }
    }, HEALTH_MS)

    return () => {
      disposed = true
      window.clearInterval(health)
      room.off(RoomEvent.TrackSubscribed, onTrack)
      room.off(RoomEvent.TrackUnsubscribed, onTrack)
      room.off(RoomEvent.ParticipantConnected, onTrack)
      const wasStarted = started
      stopLocal()
      if (wasStarted) {
        try { socket.emit('transcript:stop', { channelId: transcriptSessionId, remote: true, remoteSpeakerId: speakerId }) } catch { /* noop */ }
      }
    }
  }, [transcriptSessionId, room, phoneNumber])
}
